/**
 * Waste Calculator - Single Source of Truth
 * 
 * This calculator provides unified waste calculation logic using physical counts.
 * It replaces multiple inconsistent waste calculation implementations.
 * 
 * Method: Physical Count Method
 * Formula: Waste = (Opening Stock + Purchases) - (Expected Usage + Closing Stock)
 * 
 * This is the CORRECT method for waste calculation - using actual physical counts
 * instead of assumptions.
 * 
 * @module inventory/calculators/WasteCalculator
 */

import type {
  Material,
  Transaction,
  PhysicalCount,
  InventorySnapshot,
  WasteCalculationResult,
  MaterialWasteDetail,
  WasteMetrics,
  UUID,
  DateRange,
} from '../types';

export interface WasteCalculatorConfig {
  /**
   * Minimum waste percentage to flag as significant
   * Default: 5%
   */
  significant_waste_threshold: number;

  /**
   * Whether to include spoilage transactions in waste
   * Default: true
   */
  include_spoilage: boolean;
}

export class WasteCalculator {
  private config: WasteCalculatorConfig;

  constructor(config?: Partial<WasteCalculatorConfig>) {
    this.config = {
      significant_waste_threshold: config?.significant_waste_threshold ?? 5,
      include_spoilage: config?.include_spoilage ?? true,
    };
  }

  /**
   * Calculate waste for a single material using physical count method
   * 
   * This is the GOLD STANDARD method for waste calculation.
   * 
   * Formula:
   * Theoretical Stock = Opening Stock + Purchases - Expected Usage
   * Waste = Theoretical Stock - Actual Stock (from physical count)
   * Waste % = (Waste / Theoretical Stock) × 100
   * 
   * @param material - Material to calculate waste for
   * @param transactions - All transactions for this material in the period
   * @param physicalCounts - Physical count records
   * @param openingSnapshot - Opening stock snapshot (if available)
   * @param dateRange - Period for calculation
   * @returns Detailed waste calculation result
   */
  calculateMaterialWaste(
    material: Material,
    transactions: Transaction[],
    physicalCounts: PhysicalCount[],
    openingSnapshot?: InventorySnapshot,
    dateRange?: DateRange
  ): WasteCalculationResult {
    // Filter transactions to the date range if provided
    const periodTransactions = dateRange
      ? transactions.filter(t => this.isInDateRange(t.created_at, dateRange))
      : transactions;

    // Step 1: Get opening stock
    const openingStock = openingSnapshot
      ? openingSnapshot.quantity
      : this.estimateOpeningStock(material, periodTransactions);

    // Step 2: Calculate total purchases (restocks) in the period
    const purchases = periodTransactions
      .filter(t => t.transaction_type === 'restock')
      .reduce((sum, t) => sum + t.quantity, 0);

    // Step 3: Calculate expected usage (deductions from recipes)
    const expectedUsage = periodTransactions
      .filter(t => t.transaction_type === 'deduction')
      .reduce((sum, t) => sum + t.quantity, 0);

    // Step 4: Calculate theoretical stock (what SHOULD be left)
    const theoreticalStock = openingStock + purchases - expectedUsage;

    // Step 5: Get actual stock from physical count (or current_stock if no recent count)
    const actualStock = this.getActualStock(material, physicalCounts, dateRange);

    // Step 6: Calculate waste
    const wasteQuantity = theoreticalStock - actualStock;
    const wastePercentage = theoreticalStock > 0 
      ? (wasteQuantity / theoreticalStock) * 100 
      : 0;
    const wasteCost = wasteQuantity * material.cost_per_unit;

    return {
      material_id: material.id,
      material_name: material.name,
      waste_quantity: Math.max(0, wasteQuantity), // Can't have negative waste
      waste_percentage: wastePercentage,
      waste_cost: wasteCost,
      breakdown: {
        opening_stock: openingStock,
        purchases,
        expected_usage: expectedUsage,
        theoretical_stock: theoreticalStock,
        actual_stock: actualStock,
      },
    };
  }

  /**
   * Calculate waste for all materials
   * 
   * @param materials - All materials to analyze
   * @param transactions - All transactions grouped by material_id
   * @param physicalCounts - All physical count records
   * @param snapshots - Opening stock snapshots
   * @param dateRange - Period for calculation
   * @returns Waste metrics for all materials
   */
  calculateTotalWaste(
    materials: Material[],
    transactions: Transaction[],
    physicalCounts: PhysicalCount[],
    snapshots?: InventorySnapshot[],
    dateRange?: DateRange
  ): WasteMetrics {
    const wasteByMaterial: MaterialWasteDetail[] = [];
    let totalWasteQuantity = 0;
    let totalWasteCost = 0;

    for (const material of materials) {
      // Get transactions for this material
      const materialTransactions = transactions.filter(
        t => t.material_id === material.id
      );

      // Get physical counts for this material
      const materialCounts = physicalCounts.filter(
        pc => pc.material_id === material.id
      );

      // Get opening snapshot if available
      const openingSnapshot = snapshots?.find(
        s => s.material_id === material.id
      );

      // Calculate waste for this material
      const wasteResult = this.calculateMaterialWaste(
        material,
        materialTransactions,
        materialCounts,
        openingSnapshot,
        dateRange
      );

      // Only include if there's actual waste
      if (wasteResult.waste_quantity > 0) {
        wasteByMaterial.push({
          material_id: material.id,
          material_name: material.name,
          opening_stock: wasteResult.breakdown.opening_stock,
          purchases: wasteResult.breakdown.purchases,
          expected_usage: wasteResult.breakdown.expected_usage,
          actual_usage: wasteResult.breakdown.expected_usage + wasteResult.waste_quantity,
          closing_stock: wasteResult.breakdown.actual_stock,
          waste_quantity: wasteResult.waste_quantity,
          waste_percentage: wasteResult.waste_percentage,
          waste_cost: wasteResult.waste_cost,
        });

        totalWasteQuantity += wasteResult.waste_quantity;
        totalWasteCost += wasteResult.waste_cost;
      }
    }

    // Calculate overall waste percentage
    const totalTheoretical = materials.reduce((sum, m) => {
      const transactions = wasteByMaterial.find(w => w.material_id === m.id);
      return sum + (transactions?.opening_stock || 0) + (transactions?.purchases || 0);
    }, 0);

    const totalWastePercentage = totalTheoretical > 0 
      ? (totalWasteQuantity / totalTheoretical) * 100 
      : 0;

    return {
      total_waste_quantity: totalWasteQuantity,
      total_waste_cost: totalWasteCost,
      waste_percentage: totalWastePercentage,
      waste_by_material: wasteByMaterial.sort((a, b) => b.waste_cost - a.waste_cost),
    };
  }

  /**
   * Estimate opening stock if no snapshot available
   * 
   * Uses the earliest transaction in the period to estimate
   */
  private estimateOpeningStock(
    material: Material,
    transactions: Transaction[]
  ): number {
    if (transactions.length === 0) {
      return material.current_stock;
    }

    // Sort transactions by date
    const sortedTransactions = [...transactions].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );

    // Work backwards from current stock
    let estimatedOpening = material.current_stock;

    for (let i = sortedTransactions.length - 1; i >= 0; i--) {
      const transaction = sortedTransactions[i];
      
      switch (transaction.transaction_type) {
        case 'restock':
        case 'adjustment_add':
          estimatedOpening -= transaction.quantity;
          break;
        case 'deduction':
        case 'adjustment_subtract':
        case 'waste':
          estimatedOpening += transaction.quantity;
          break;
      }
    }

    return Math.max(0, estimatedOpening);
  }

  /**
   * Get actual stock from most recent physical count
   */
  private getActualStock(
    material: Material,
    physicalCounts: PhysicalCount[],
    dateRange?: DateRange
  ): number {
    if (physicalCounts.length === 0) {
      return material.current_stock;
    }

    // Filter by date range if provided
    const relevantCounts = dateRange
      ? physicalCounts.filter(pc => this.isInDateRange(pc.count_date, dateRange))
      : physicalCounts;

    if (relevantCounts.length === 0) {
      return material.current_stock;
    }

    // Get most recent count
    const latestCount = relevantCounts.sort(
      (a, b) => new Date(b.count_date).getTime() - new Date(a.count_date).getTime()
    )[0];

    return latestCount.counted_quantity;
  }

  /**
   * Check if a date is within a date range
   */
  private isInDateRange(date: string, dateRange: DateRange): boolean {
    const checkDate = new Date(date);
    const startDate = new Date(dateRange.start_date);
    const endDate = new Date(dateRange.end_date);
    
    return checkDate >= startDate && checkDate <= endDate;
  }

  /**
   * Identify materials with significant waste
   * 
   * @param wasteMetrics - Calculated waste metrics
   * @returns Materials with waste above threshold
   */
  identifySignificantWaste(wasteMetrics: WasteMetrics): MaterialWasteDetail[] {
    return wasteMetrics.waste_by_material.filter(
      w => w.waste_percentage >= this.config.significant_waste_threshold
    );
  }

  /**
   * Calculate waste trend over multiple periods
   * 
   * @param materials - Materials to analyze
   * @param transactions - All transactions
   * @param physicalCounts - All physical counts
   * @param periods - Array of date ranges to analyze
   * @returns Waste trend data
   */
  calculateWasteTrend(
    materials: Material[],
    transactions: Transaction[],
    physicalCounts: PhysicalCount[],
    periods: DateRange[]
  ): Array<{
    period: DateRange;
    waste_cost: number;
    waste_percentage: number;
    trend: 'increasing' | 'decreasing' | 'stable';
  }> {
    const trends = periods.map(period => {
      const metrics = this.calculateTotalWaste(
        materials,
        transactions,
        physicalCounts,
        undefined,
        period
      );

      return {
        period,
        waste_cost: metrics.total_waste_cost,
        waste_percentage: metrics.waste_percentage,
        trend: 'stable' as 'increasing' | 'decreasing' | 'stable',
      };
    });

    // Calculate trends by comparing periods
    for (let i = 1; i < trends.length; i++) {
      const current = trends[i];
      const previous = trends[i - 1];
      
      const percentChange = previous.waste_cost > 0
        ? ((current.waste_cost - previous.waste_cost) / previous.waste_cost) * 100
        : 0;

      if (percentChange > 10) {
        current.trend = 'increasing';
      } else if (percentChange < -10) {
        current.trend = 'decreasing';
      } else {
        current.trend = 'stable';
      }
    }

    return trends;
  }

  /**
   * Generate waste reduction recommendations
   * 
   * @param wasteMetrics - Calculated waste metrics
   * @returns Actionable recommendations
   */
  generateWasteRecommendations(wasteMetrics: WasteMetrics): string[] {
    const recommendations: string[] = [];

    // Overall waste assessment
    if (wasteMetrics.waste_percentage > 10) {
      recommendations.push('🚨 Critical: Overall waste exceeds 10%. Immediate action required.');
    } else if (wasteMetrics.waste_percentage > 5) {
      recommendations.push('⚠️ Warning: Waste levels are above target. Review procedures.');
    } else {
      recommendations.push('✅ Waste levels are within acceptable range.');
    }

    // Top waste contributors
    const topWaste = wasteMetrics.waste_by_material.slice(0, 3);
    if (topWaste.length > 0) {
      recommendations.push('📊 Top waste contributors:');
      topWaste.forEach(w => {
        recommendations.push(
          `   • ${w.material_name}: ${w.waste_percentage.toFixed(1)}% waste (${w.waste_cost.toFixed(2)} cost)`
        );
      });
    }

    // Specific recommendations
    const significantWaste = this.identifySignificantWaste(wasteMetrics);
    if (significantWaste.length > 0) {
      recommendations.push('💡 Recommendations:');
      recommendations.push('   • Conduct more frequent physical counts for high-waste items');
      recommendations.push('   • Review storage procedures and shelf-life management');
      recommendations.push('   • Train staff on portion control and waste prevention');
      recommendations.push('   • Consider adjusting order quantities to reduce spoilage');
    }

    return recommendations;
  }
}

// Export singleton instance with default configuration
export const wasteCalculator = new WasteCalculator();
